import { useState, useEffect, useRef, useCallback } from 'react';
import glossaryService from '../services/glossaryService';
import { flattenV2Entries, validateDisplayName, getGlossaryDisplayName } from '../utils/glossaryUtils';
import { SUPPORTED_LANGUAGES, getLanguageName } from '../data/supportedLanguages';
import Button from './ui/Button';
import {
    Upload, X, Trash2, FileText, Loader2,
    CheckCircle, AlertCircle, Clock, Save, RotateCcw, Eye, Edit2
} from 'lucide-react';
import './AIGlossaryGenerator.css';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ACCEPTED_TYPES = ['.pdf', '.jpg', '.jpeg', '.png'];
const ACCEPTED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];

// Default target languages for new generations
const DEFAULT_TARGETS = ['ko-KR', 'ja-JP', 'zh-CN', 'es-ES', 'fr-FR', 'de-DE', 'pt-BR'];

// Map frontend BCP-47 codes to backend short codes
const toBackendCode = (code) => {
    const keepFull = ['zh-CN', 'zh-TW', 'pt-BR'];
    if (keepFull.includes(code)) return code;
    return code.split('-')[0];
};

const POLL_INTERVAL_MS = 10000; // Poll every 10 seconds

// Processing phase labels mapped to backend status values
const PROCESSING_PHASES = [
    { label: 'Extracting text from document...', status: 'extracting' },
    { label: 'Detecting domain and context...', status: 'detecting-domain' },
    { label: 'Extracting key terms...', status: 'extracting-terms' },
    { label: 'Generating translations...', status: 'translating' }
];

// Map backend status to phase index
const STATUS_TO_PHASE = {
    'extracting': 0,
    'detecting-domain': 1,
    'extracting-terms': 2,
    'translating': 3
};

const AIGlossaryGenerator = ({ tenantId, tenants, isAdmin, setTenantId, onGlossarySaved }) => {
    // Phase: 'configure' | 'processing' | 'review'
    const [phase, setPhase] = useState('configure');

    // Configure phase state
    const [file, setFile] = useState(null);
    const [displayName, setDisplayName] = useState('');
    const [selectedLanguages, setSelectedLanguages] = useState(DEFAULT_TARGETS);
    const [fileError, setFileError] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    // Processing phase state
    const [elapsed, setElapsed] = useState(0);
    const [processingPhase, setProcessingPhase] = useState(0);
    const [jobId, setJobId] = useState(null);
    const timerRef = useRef(null);
    const pollRef = useRef(null);
    const cancelledRef = useRef(false);

    // Review phase state
    const [result, setResult] = useState(null);
    const [editDisplayName, setEditDisplayName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [entries, setEntries] = useState([]);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    // Saved glossaries list
    const [savedGlossaries, setSavedGlossaries] = useState([]);
    const [loadingGlossaries, setLoadingGlossaries] = useState(false);
    const [expandedGlossary, setExpandedGlossary] = useState(null);
    const [expandedEntries, setExpandedEntries] = useState([]);
    const [editingGlossary, setEditingGlossary] = useState(null);
    const [editedEntries, setEditedEntries] = useState([]);
    const [savingGlossaryEdit, setSavingGlossaryEdit] = useState(false);

    // Load saved glossaries when tenant changes
    const loadSavedGlossaries = useCallback(async () => {
        if (!tenantId) {
            setSavedGlossaries([]);
            return;
        }
        setLoadingGlossaries(true);
        try {
            const response = await glossaryService.listGlossaries(tenantId);
            setSavedGlossaries(response['tenant-glossaries'] || []);
        } catch (err) {
            console.error('[AIGlossaryGenerator] Failed to load glossaries:', err);
        } finally {
            setLoadingGlossaries(false);
        }
    }, [tenantId]);

    useEffect(() => {
        loadSavedGlossaries();
    }, [loadSavedGlossaries]);

    // Elapsed timer during processing
    useEffect(() => {
        if (phase === 'processing') {
            setElapsed(0);
            timerRef.current = setInterval(() => {
                setElapsed(prev => prev + 1);
            }, 1000);
            return () => clearInterval(timerRef.current);
        }
        return () => clearInterval(timerRef.current);
    }, [phase]);

    // Poll for job status during processing
    useEffect(() => {
        if (phase !== 'processing' || !jobId || !tenantId) return;

        cancelledRef.current = false;

        const poll = async () => {
            if (cancelledRef.current) return;
            try {
                const status = await glossaryService.getIngestionStatus(jobId, tenantId);

                if (cancelledRef.current) return;

                // Update phase based on real backend status
                // #4288: a held generation stays "processing"; its visible step is the pipeline stage.
                const phaseIndex = STATUS_TO_PHASE[status.PipelineStage || status.pipelineStage || status.Status || status.status];
                if (phaseIndex !== undefined) {
                    setProcessingPhase(phaseIndex);
                }

                const jobStatus = status.Status || status.status;

                if (jobStatus === 'complete') {
                    clearInterval(pollRef.current);
                    const data = status.Result || status.result;
                    if (data) {
                        setResult(data);
                        setEditDisplayName(displayName);
                        setEditDescription(data.metadata?.domain
                            ? `AI-generated glossary for ${data.metadata.domain} domain`
                            : 'AI-generated glossary');
                        setEntries(data.glossary?.entries || data.entries || []);
                    }
                    setPhase('review');
                } else if (jobStatus === 'failed') {
                    clearInterval(pollRef.current);
                    setError(status.ErrorMessage || status.errorMessage || 'Generation failed');
                    setPhase('configure');
                } else if (jobStatus === 'no-terms') {
                    clearInterval(pollRef.current);
                    setError('No domain-specific terms could be extracted from the document.');
                    setPhase('configure');
                }
            } catch (err) {
                if (cancelledRef.current) return;
                console.error('[AIGlossaryGenerator] Poll error:', err);
                // Don't stop polling on transient errors — keep trying
            }
        };

        // Initial poll after a short delay, then every POLL_INTERVAL_MS
        const initialTimeout = setTimeout(poll, 3000);
        pollRef.current = setInterval(poll, POLL_INTERVAL_MS);

        return () => {
            clearTimeout(initialTimeout);
            clearInterval(pollRef.current);
        };
    }, [phase, jobId, tenantId, displayName]);

    // File handling
    const validateFile = (f) => {
        if (!f) return 'Please select a file';
        if (!ACCEPTED_MIME.includes(f.type)) {
            return 'Invalid file type. Accepted: PDF, JPEG, PNG';
        }
        if (f.size > MAX_FILE_SIZE) {
            return `File too large (${(f.size / 1024 / 1024).toFixed(1)}MB). Maximum: 50MB`;
        }
        return '';
    };

    const handleFileSelect = (f) => {
        const err = validateFile(f);
        setFileError(err);
        if (!err) {
            setFile(f);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files[0];
        if (f) handleFileSelect(f);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    const handleFileInputChange = (e) => {
        const f = e.target.files[0];
        if (f) handleFileSelect(f);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const toggleLanguage = (code) => {
        setSelectedLanguages(prev =>
            prev.includes(code)
                ? prev.filter(c => c !== code)
                : [...prev, code]
        );
    };

    // Generate glossary (async with polling)
    const handleGenerate = async () => {
        if (!file || !tenantId || !displayName.trim()) return;

        const nameValidation = validateDisplayName(displayName);
        if (!nameValidation.valid) {
            setError(nameValidation.error);
            return;
        }

        setError('');
        setProcessingPhase(0);
        setPhase('processing');

        try {
            const backendCodes = selectedLanguages.map(toBackendCode);
            const data = await glossaryService.generateGlossaryAsync(
                file,
                backendCodes,
                'en',
                tenantId
            );

            // Store jobId — polling useEffect will take over
            setJobId(data.jobId);
        } catch (err) {
            setError(err.message || 'Failed to start generation');
            setPhase('configure');
        }
    };

    const handleCancel = () => {
        cancelledRef.current = true;
        clearInterval(pollRef.current);
        resetToConfig();
    };

    const resetToConfig = () => {
        setPhase('configure');
        setFile(null);
        setDisplayName('');
        setFileError('');
        setResult(null);
        setEntries([]);
        setError('');
        setElapsed(0);
        setJobId(null);
        cancelledRef.current = true;
    };

    // Remove an entry from review results
    const handleRemoveEntry = (index) => {
        setEntries(prev => prev.filter((_, i) => i !== index));
        setEditingCell(null);
        setEditingText('');
    };

    // Inline translation edit state
    const [editingCell, setEditingCell] = useState(null); // { entryIdx, tIdx }
    const [editingText, setEditingText] = useState('');

    const handleStartEditTranslation = (entryIdx, tIdx) => {
        setEditingCell({ entryIdx, tIdx });
        setEditingText(entries[entryIdx].translations[tIdx].targetText);
    };

    const handleSaveEditTranslation = () => {
        if (!editingText.trim()) return;
        const { entryIdx, tIdx } = editingCell;
        setEntries(prev => prev.map((entry, ei) =>
            ei !== entryIdx ? entry : {
                ...entry,
                translations: entry.translations.map((t, ti) =>
                    ti !== tIdx ? t : { ...t, targetText: editingText }
                )
            }
        ));
        setEditingCell(null);
        setEditingText('');
    };

    const handleCancelEditTranslation = () => {
        setEditingCell(null);
        setEditingText('');
    };

    // Save as tenant glossary
    const handleSave = async () => {
        const nameValidation = validateDisplayName(editDisplayName);
        if (!nameValidation.valid) {
            setError(nameValidation.error);
            return;
        }

        if (entries.length === 0) {
            setError('No entries to save. Add at least one entry.');
            return;
        }

        setSaving(true);
        setError('');
        try {
            const flatEntries = flattenV2Entries(entries);
            await glossaryService.createGlossary({
                tenantId,
                displayName: editDisplayName.trim(),
                description: editDescription.trim(),
                entries: flatEntries
            });
            await loadSavedGlossaries();
            resetToConfig();
            if (onGlossarySaved) onGlossarySaved();
        } catch (err) {
            setError(err.message || 'Failed to save glossary');
        } finally {
            setSaving(false);
        }
    };

    // Expand a saved glossary to view entries
    const handleExpandGlossary = async (glossary) => {
        const name = glossary.tenantGlossaryName;
        if (expandedGlossary === name) {
            setExpandedGlossary(null);
            setExpandedEntries([]);
            return;
        }
        try {
            const full = await glossaryService.getGlossary(tenantId, name);
            setExpandedGlossary(name);
            setExpandedEntries(full.entries || []);
        } catch (err) {
            console.error('[AIGlossaryGenerator] Failed to load glossary entries:', err);
        }
    };

    // Start editing a saved glossary's entries
    const handleStartEditGlossary = (glossaryName) => {
        setEditingGlossary(glossaryName);
        setEditedEntries(expandedEntries.map(e => ({ ...e })));
    };

    const handleCancelEditGlossary = () => {
        setEditingGlossary(null);
        setEditedEntries([]);
    };

    const handleEditEntryField = (index, field, value) => {
        setEditedEntries(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const handleDeleteEditEntry = (index) => {
        setEditedEntries(prev => prev.filter((_, i) => i !== index));
    };

    const handleSaveGlossaryEdit = async (glossary) => {
        if (editedEntries.length === 0) {
            setError('Cannot save an empty glossary.');
            return;
        }

        setSavingGlossaryEdit(true);
        setError('');
        try {
            await glossaryService.updateGlossary({
                tenantId,
                tenantGlossaryName: glossary.tenantGlossaryName,
                displayName: getGlossaryDisplayName(glossary),
                description: glossary.description || '',
                entries: editedEntries.map(e => ({
                    sourceLanguage: e.sourceLanguage || 'en',
                    targetLanguage: e.targetLanguage,
                    sourceText: e.sourceText,
                    targetText: e.targetText
                }))
            });
            // Refresh
            setEditingGlossary(null);
            setEditedEntries([]);
            setExpandedEntries(editedEntries);
            await loadSavedGlossaries();
        } catch (err) {
            setError(err.message || 'Failed to save glossary changes');
        } finally {
            setSavingGlossaryEdit(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const formatElapsed = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return m > 0 ? `${m}m ${s}s` : `${s}s`;
    };

    const getConfidenceClass = (confidence) => {
        if (confidence >= 0.8) return 'ai-gen-confidence-high';
        if (confidence >= 0.6) return 'ai-gen-confidence-medium';
        return 'ai-gen-confidence-low';
    };

    const canGenerate = file && tenantId && displayName.trim() && selectedLanguages.length > 0 && !fileError;

    // --- Render Phases ---

    const renderConfigurePhase = () => (
        <div className="ai-gen-configure">
            {/* Tenant selector */}
            <div className="ai-gen-field">
                <label className="ui-input-label">Tenant *</label>
                {isAdmin ? (
                    <select
                        className="ui-select"
                        value={tenantId}
                        onChange={(e) => setTenantId(e.target.value)}
                    >
                        <option value="">-- Select Tenant --</option>
                        {tenants.map(t => (
                            <option key={t.RowKey} value={t.RowKey}>
                                {t.TenantName}
                            </option>
                        ))}
                    </select>
                ) : (
                    <div className="ai-gen-tenant-display">{tenants[0]?.TenantName || tenantId}</div>
                )}
            </div>

            {/* Display Name */}
            <div className="ai-gen-field">
                <label className="ui-input-label">Glossary Display Name *</label>
                <input
                    type="text"
                    className="ui-input"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="e.g., Medical Conference Terms, Legal Contract Glossary"
                    maxLength={128}
                />
            </div>

            {/* File upload dropzone */}
            <div className="ai-gen-field">
                <label className="ui-input-label">Upload Document *</label>
                <div
                    className={`ai-gen-dropzone ${dragOver ? 'ai-gen-dropzone-active' : ''} ${file ? 'ai-gen-dropzone-has-file' : ''}`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPTED_TYPES.join(',')}
                        onChange={handleFileInputChange}
                        style={{ display: 'none' }}
                    />
                    {file ? (
                        <div className="ai-gen-file-info">
                            <FileText size={24} />
                            <div>
                                <span className="ai-gen-file-name">{file.name}</span>
                                <span className="ai-gen-file-size">{formatFileSize(file.size)}</span>
                            </div>
                            <button
                                className="ai-gen-file-remove"
                                onClick={(e) => { e.stopPropagation(); setFile(null); setFileError(''); }}
                            >
                                <X size={16} />
                            </button>
                        </div>
                    ) : (
                        <div className="ai-gen-dropzone-content">
                            <Upload size={32} />
                            <p>Drag and drop a document here, or click to browse</p>
                            <span className="ai-gen-dropzone-hint">PDF, JPEG, or PNG (max 50MB)</span>
                        </div>
                    )}
                </div>
                {fileError && <span className="ai-gen-error-text">{fileError}</span>}
            </div>

            {/* Target Languages */}
            <div className="ai-gen-field">
                <label className="ui-input-label">Target Languages ({selectedLanguages.length} selected)</label>
                <div className="ai-gen-language-grid">
                    {SUPPORTED_LANGUAGES.filter(l => l.code !== 'en').map(lang => (
                        <button
                            key={lang.code}
                            className={`ai-gen-lang-pill ${selectedLanguages.includes(lang.code) ? 'ai-gen-lang-pill-active' : ''}`}
                            onClick={() => toggleLanguage(lang.code)}
                            type="button"
                        >
                            {lang.name.split(' - ')[0]}
                        </button>
                    ))}
                </div>
            </div>

            {/* Generate button */}
            <div className="ai-gen-actions">
                <Button
                    onClick={handleGenerate}
                    disabled={!canGenerate}
                    isLoading={false}
                >
                    <FileText size={18} /> Generate Glossary
                </Button>
            </div>
        </div>
    );

    const renderProcessingPhase = () => (
        <div className="ai-gen-processing">
            <div className="ai-gen-processing-icon">
                <FileText size={48} className="ai-gen-pulse" />
            </div>

            <div className="ai-gen-processing-phases">
                {PROCESSING_PHASES.map((p, i) => (
                    <div key={i} className={`ai-gen-phase-item ${i < processingPhase ? 'ai-gen-phase-done' : ''} ${i === processingPhase ? 'ai-gen-phase-active' : ''}`}>
                        {i < processingPhase ? (
                            <CheckCircle size={18} />
                        ) : i === processingPhase ? (
                            <Loader2 size={18} className="animate-spin" />
                        ) : (
                            <Clock size={18} />
                        )}
                        <span>{p.label}</span>
                    </div>
                ))}
            </div>

            <div className="ai-gen-elapsed">
                <Clock size={16} />
                <span>Elapsed: {formatElapsed(elapsed)}</span>
            </div>

            {elapsed > 300 && (
                <div className="ai-gen-warning">
                    <AlertCircle size={16} />
                    Processing is taking longer than usual. The request is still active.
                </div>
            )}

            <Button variant="ghost" onClick={handleCancel}>
                Cancel
            </Button>
        </div>
    );

    const renderReviewPhase = () => {
        const metadata = result?.metadata || {};
        const totalTranslations = entries.reduce(
            (sum, e) => sum + (e.translations?.length || 0), 0
        );

        return (
            <div className="ai-gen-review">
                {/* Metadata banner */}
                <div className="ai-gen-metadata">
                    {metadata.domain && (
                        <div className="ai-gen-meta-item">
                            <strong>Domain:</strong> {metadata.domain}
                            {metadata.domainConfidence != null && (
                                <span className={`ai-gen-confidence-badge ${getConfidenceClass(metadata.domainConfidence)}`}>
                                    {Math.round(metadata.domainConfidence * 100)}%
                                </span>
                            )}
                        </div>
                    )}
                    <div className="ai-gen-meta-item">
                        <strong>Terms:</strong> {entries.length}
                    </div>
                    <div className="ai-gen-meta-item">
                        <strong>Translations:</strong> {totalTranslations}
                    </div>
                    {metadata.pageCount > 0 && (
                        <div className="ai-gen-meta-item">
                            <strong>Pages:</strong> {metadata.pageCount}
                        </div>
                    )}
                    {metadata.textLength > 0 && (
                        <div className="ai-gen-meta-item">
                            <strong>Text:</strong> {metadata.textLength.toLocaleString()} chars
                        </div>
                    )}
                </div>

                {/* Editable fields */}
                <div className="ai-gen-edit-fields">
                    <div className="ai-gen-field">
                        <label className="ui-input-label">Display Name</label>
                        <input
                            type="text"
                            className="ui-input"
                            value={editDisplayName}
                            onChange={(e) => setEditDisplayName(e.target.value)}
                            maxLength={128}
                        />
                    </div>
                    <div className="ai-gen-field">
                        <label className="ui-input-label">Description</label>
                        <input
                            type="text"
                            className="ui-input"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                        />
                    </div>
                </div>

                {/* Results table */}
                <div className="ai-gen-results-table-container">
                    <table className="ai-gen-results-table">
                        <thead>
                            <tr>
                                <th>Source Term</th>
                                <th>Language</th>
                                <th>Translation</th>
                                <th>Confidence</th>
                                <th></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((entry, entryIdx) => (
                                (entry.translations || []).map((t, tIdx) => (
                                    <tr key={`${entryIdx}-${tIdx}`}>
                                        {tIdx === 0 && (
                                            <td rowSpan={entry.translations?.length || 1} className="ai-gen-source-cell">
                                                {entry.sourceText}
                                            </td>
                                        )}
                                        <td>{getLanguageName(t.targetLanguage) || t.targetLanguage}</td>
                                        <td>
                                            {editingCell?.entryIdx === entryIdx && editingCell?.tIdx === tIdx ? (
                                                <div className="ai-gen-translation-edit">
                                                    <input
                                                        className="ai-gen-translation-edit-input"
                                                        value={editingText}
                                                        onChange={e => setEditingText(e.target.value)}
                                                        autoFocus
                                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSaveEditTranslation(); } else if (e.key === 'Escape') { e.preventDefault(); handleCancelEditTranslation(); } }}
                                                    />
                                                </div>
                                            ) : (
                                                <span>{t.targetText}</span>
                                            )}
                                        </td>
                                        <td>
                                            {t.confidence != null && (
                                                <span className={`ai-gen-confidence-badge ${getConfidenceClass(t.confidence)}`}>
                                                    {Math.round(t.confidence * 100)}%
                                                </span>
                                            )}
                                        </td>
                                        <td className="ai-gen-row-actions">
                                            {editingCell?.entryIdx === entryIdx && editingCell?.tIdx === tIdx ? (
                                                <>
                                                    <button type="button" className="ai-gen-edit-button" onClick={handleSaveEditTranslation} aria-label="Save changes" title="Save"><CheckCircle size={14} className="ai-gen-edit-icon save" aria-hidden="true" /></button>
                                                    <button type="button" className="ai-gen-edit-button" onClick={handleCancelEditTranslation} aria-label="Cancel editing" title="Cancel"><X size={14} className="ai-gen-edit-icon cancel" aria-hidden="true" /></button>
                                                </>
                                            ) : (
                                                <button type="button" className="ai-gen-edit-button" onClick={() => handleStartEditTranslation(entryIdx, tIdx)} aria-label="Edit translation" title="Edit translation"><Edit2 size={14} className="ai-gen-edit-icon edit" aria-hidden="true" /></button>
                                            )}
                                        </td>
                                        {tIdx === 0 && (
                                            <td rowSpan={entry.translations?.length || 1} className="ai-gen-delete-cell">
                                                <button
                                                    className="ai-gen-delete-btn"
                                                    onClick={() => handleRemoveEntry(entryIdx)}
                                                    title="Remove this term"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            ))}
                            {entries.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="ai-gen-empty-row">
                                        All entries removed
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Action buttons */}
                <div className="ai-gen-review-actions">
                    <Button onClick={handleSave} isLoading={saving} disabled={entries.length === 0}>
                        <Save size={18} /> Save as Tenant Glossary
                    </Button>
                    <Button variant="secondary" onClick={resetToConfig}>
                        <RotateCcw size={18} /> Generate Another
                    </Button>
                    <Button variant="ghost" onClick={resetToConfig}>
                        Discard
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <div className="ai-gen-container">
            <div className="ai-gen-header">
                <h2>AI Glossary Generator</h2>
                <p className="ai-gen-subtitle">
                    Upload a document to automatically extract domain-specific terms and generate multilingual translations.
                </p>
            </div>

            {error && (
                <div className="ai-gen-error-banner">
                    <AlertCircle size={18} />
                    <span>{error}</span>
                    <button className="ai-gen-error-dismiss" onClick={() => setError('')}>
                        <X size={14} />
                    </button>
                </div>
            )}

            {phase === 'configure' && renderConfigurePhase()}
            {phase === 'processing' && renderProcessingPhase()}
            {phase === 'review' && renderReviewPhase()}

            {/* Saved glossaries section (visible in configure and review phases) */}
            {phase !== 'processing' && tenantId && (
                <div className="ai-gen-saved-section">
                    <h3>Saved Glossaries</h3>
                    {loadingGlossaries ? (
                        <div className="ai-gen-loading-small">
                            <Loader2 size={20} className="animate-spin" />
                            <span>Loading...</span>
                        </div>
                    ) : savedGlossaries.length === 0 ? (
                        <p className="ai-gen-empty-text">No glossaries saved for this tenant yet.</p>
                    ) : (
                        <div className="ai-gen-saved-grid">
                            {savedGlossaries.map((g) => (
                                <div key={g.tenantGlossaryName} className="ai-gen-saved-card">
                                    <div className="ai-gen-saved-card-header">
                                        <h4>{getGlossaryDisplayName(g)}</h4>
                                        <span className="ai-gen-badge">v{g.version}</span>
                                    </div>
                                    <div className="ai-gen-saved-card-meta">
                                        <span>{g.entryCount} entries</span>
                                        <span>{new Date(g.updatedAt).toLocaleDateString()}</span>
                                    </div>
                                    {g.description && (
                                        <p className="ai-gen-saved-card-desc">{g.description}</p>
                                    )}
                                    <div className="ai-gen-saved-card-actions">
                                        <button
                                            className="ai-gen-view-btn"
                                            onClick={() => handleExpandGlossary(g)}
                                        >
                                            <Eye size={14} />
                                            {expandedGlossary === g.tenantGlossaryName ? 'Hide' : 'View'} Entries
                                        </button>
                                        {expandedGlossary === g.tenantGlossaryName && editingGlossary !== g.tenantGlossaryName && (
                                            <button
                                                className="ai-gen-view-btn"
                                                onClick={() => handleStartEditGlossary(g.tenantGlossaryName)}
                                            >
                                                <Edit2 size={14} /> Edit
                                            </button>
                                        )}
                                    </div>
                                    {expandedGlossary === g.tenantGlossaryName && (
                                        <div className="ai-gen-expanded-entries">
                                            {editingGlossary === g.tenantGlossaryName ? (
                                                <>
                                                    <table className="ai-gen-mini-table ai-gen-mini-table-edit">
                                                        <thead>
                                                            <tr>
                                                                <th>Source</th>
                                                                <th>Target</th>
                                                                <th>Translation</th>
                                                                <th></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {editedEntries.map((e, i) => (
                                                                <tr key={i}>
                                                                    <td>
                                                                        <input
                                                                            type="text"
                                                                            className="ai-gen-edit-input"
                                                                            value={e.sourceText}
                                                                            onChange={(ev) => handleEditEntryField(i, 'sourceText', ev.target.value)}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <input
                                                                            type="text"
                                                                            className="ai-gen-edit-input ai-gen-edit-input-narrow"
                                                                            value={e.targetLanguage}
                                                                            onChange={(ev) => handleEditEntryField(i, 'targetLanguage', ev.target.value)}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <input
                                                                            type="text"
                                                                            className="ai-gen-edit-input"
                                                                            value={e.targetText}
                                                                            onChange={(ev) => handleEditEntryField(i, 'targetText', ev.target.value)}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <button
                                                                            className="ai-gen-delete-btn"
                                                                            onClick={() => handleDeleteEditEntry(i)}
                                                                            title="Remove entry"
                                                                        >
                                                                            <Trash2 size={12} />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                            {editedEntries.length === 0 && (
                                                                <tr>
                                                                    <td colSpan="4" className="ai-gen-more-text">
                                                                        All entries removed
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                    <div className="ai-gen-edit-actions">
                                                        <Button
                                                            onClick={() => handleSaveGlossaryEdit(g)}
                                                            isLoading={savingGlossaryEdit}
                                                            disabled={editedEntries.length === 0}
                                                        >
                                                            <Save size={14} /> Save Changes
                                                        </Button>
                                                        <Button variant="ghost" onClick={handleCancelEditGlossary}>
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                </>
                                            ) : expandedEntries.length === 0 ? (
                                                <p className="ai-gen-empty-text">No entries</p>
                                            ) : (
                                                <table className="ai-gen-mini-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Source</th>
                                                            <th>Target</th>
                                                            <th>Translation</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {expandedEntries.map((e, i) => (
                                                            <tr key={i}>
                                                                <td>{e.sourceText}</td>
                                                                <td>{e.targetLanguage}</td>
                                                                <td>{e.targetText}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AIGlossaryGenerator;
